import React, { useState, useCallback, useEffect, useRef } from 'react';
import Box from './Box';
import { Home } from './Icon';

interface OptimizedImageProps {
  src?: string | null;
  style?: React.CSSProperties;
  fallbackIcon?: React.ReactNode;
  onError?: () => void;
  onLoad?: () => void;
  timeout?: number;
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  style = {},
  fallbackIcon = <Home size={20} color="#6c757d" />,
  onError,
  onLoad,
  timeout = 10000 // Increased timeout to 10 seconds
}) => {
  const [imageError, setImageError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<number | null>(null);

  // Add timeout for image loading
  useEffect(() => {
    if (!src || imageError || isLoaded) return;
    
    setIsLoading(true);
    timerRef.current = window.setTimeout(() => {
      if (!isLoaded && isLoading) {
        console.warn(`Image load timeout for: ${src}`);
        setImageError(true);
        setIsLoading(false);
        onError?.();
      }
    }, timeout);
    
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [src, isLoaded, imageError, onError, timeout, isLoading]);

  const handleImageError = useCallback(() => {
    console.warn(`Image failed to load: ${src}`);
    setImageError(true);
    setIsLoaded(false);
    setIsLoading(false);
    onError?.();
  }, [onError, src]);

  const handleImageLoad = useCallback(() => {
    setIsLoaded(true);
    setIsLoading(false);
    onLoad?.();
    // Clear timeout on successful load
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [onLoad]);

  // Reset state on src change
  useEffect(() => {
    setIsLoaded(false);
    setImageError(false);
    setIsLoading(false);
    // Clear existing timeout
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [src]);

  // If no src → show fallback
  if (!src) {
    return (
      <Box
        sx={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          border: '1px solid #e9ecef',
          minHeight: style.height || '40px',
          minWidth: style.width || '40px'
        }}
      >
        {fallbackIcon}
      </Box>
    );
  }

  // If error → show fallback
  if (imageError) {
    return (
      <Box
        sx={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          border: '1px solid #e9ecef',
          minHeight: style.height || '40px',
          minWidth: style.width || '40px'
        }}
      >
        {fallbackIcon}
      </Box>
    );
  }

  // Show loading state while image is loading
  if (isLoading && !isLoaded) {
    return (
      <Box
        sx={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          border: '1px solid #e9ecef',
          minHeight: style.height || '40px',
          minWidth: style.width || '40px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
            animation: 'shimmer 1.5s infinite'
          }}
        />
        <div style={{ zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {fallbackIcon}
        </div>
        {/* Must stay mounted while loading: without an <img> in the DOM neither onLoad nor
            onError can fire, so a src change would always run into the timeout and end on the fallback. */}
        <img
          src={src}
          onError={handleImageError}
          onLoad={handleImageLoad}
          alt=""
          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
        />
      </Box>
    );
  }

  // Show actual image
  return (
    <img 
      src={src} 
      style={style} 
      onError={handleImageError} 
      onLoad={handleImageLoad}
      alt=""
    />
  );
};

export default OptimizedImage; 